import type { FC } from 'react';
import React, { useEffect } from 'react';
import { useRoutes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { SnackbarProvider } from 'notistack';
import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import GlobalStyles from 'components/GlobalStyles';
import SplashScreen from 'pages/authentication/SplashScreen';
import useAuth from './hooks/useAuth';
import useScrollReset from './hooks/useScrollReset';
import useSettings from './hooks/useSettings';
import routes from './routes';
import { createTheme } from './theme';
import ProfileValidator from './pages/authentication/profile/ProfileValidator';
import { KeycloakProvider } from './contexts/KeycloakContext';
import { usePostHog } from '@posthog/react';


const queryClient = new QueryClient();
const App: FC<React.PropsWithChildren<unknown>> = () => {
  const content = useRoutes(routes);
  const { settings } = useSettings();
  const posthog = usePostHog();

  const { isInitialized, user, loadUser } = useAuth();
  useScrollReset();

  useEffect(() => {
    if (!isInitialized) {
      loadUser();
    }
  }, []);

  useEffect(() => {
    if (user) {
      posthog?.identify(user.id, { email: user.email });
      posthog?.group('company', user.partnerId);
    }
  }, [user]);

  const theme = createTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
    theme: settings.theme
  });


  return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider
                dense
                maxSnack={3}
            >
              <ToastContainer/>
              <CssBaseline/>
              <GlobalStyles/>
              {
                isInitialized ?
                  (
                        <ProfileValidator>
                          {content}
                        </ProfileValidator>
                  ) :
                  (
                        <SplashScreen/>
                  )
              }
            </SnackbarProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </StyledEngineProvider>
  );
};

export const AppContainer = ()=>{
  return <KeycloakProvider><App /></KeycloakProvider>;
};

export default AppContainer;
